import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, Mail, Send, Users, CheckCircle, ChevronDown, ChevronRight, UserCheck, UserX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const GuestRelationshipManager = ({ user }) => {
  const navigate = useNavigate();
  const [guests, setGuests] = useState([]);
  const [selectedGuests, setSelectedGuests] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Collapsible sections state - all closed by default
  const [expandedSections, setExpandedSections] = useState({
    all: false,
    confirmed: false,
    pending: false,
    declined: false
  });

  useEffect(() => {
    loadGuests();
  }, [user.id]);

  const loadGuests = async () => {
    try {
      const response = await axios.get(`${API}/guests/${user.id}`);
      setGuests(response.data);
    } catch (error) {
      console.error('Error loading guests:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSelectAll = () => {
    if (selectedGuests.length === guests.length) {
      setSelectedGuests([]);
    } else {
      setSelectedGuests(guests.map(g => g.id));
    }
  };

  const handleSelectGuest = (guestId) => {
    if (selectedGuests.includes(guestId)) {
      setSelectedGuests(selectedGuests.filter(id => id !== guestId));
    } else {
      setSelectedGuests([...selectedGuests, guestId]);
    }
  };

  const handleSelectGroup = (guestIds) => {
    const allSelected = guestIds.every(id => selectedGuests.includes(id));
    if (allSelected) {
      setSelectedGuests(selectedGuests.filter(id => !guestIds.includes(id)));
    } else {
      const newSelected = [...new Set([...selectedGuests, ...guestIds])];
      setSelectedGuests(newSelected);
    }
  };

  const handleSendEmails = async () => {
    if (selectedGuests.length === 0) {
      toast.error('Please select at least one guest');
      return;
    }
    if (!emailSubject || !emailBody) {
      toast.error('Please enter subject and message');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/guests/send-email`, {
        user_id: user.id,
        guest_ids: selectedGuests,
        subject: emailSubject,
        body: emailBody
      });
      
      const { sent_count, failed_count, failed_guests } = response.data;
      
      if (failed_count > 0) {
        toast.success(
          `Sent to ${sent_count} guest(s). ${failed_count} failed: ${failed_guests.join(', ')}`,
          { duration: 6000 }
        );
      } else {
        toast.success(
          `Successfully sent ${sent_count} personalized email(s)! 📧`,
          { duration: 4000 }
        );
      }
      
      setEmailSubject('');
      setEmailBody('');
      setSelectedGuests([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const emailTemplates = [
    {
      name: 'Save the Date',
      subject: 'Save the Date - Our Wedding!',
      body: `Dear [Guest Name],

We're thrilled to announce that we're getting married!

Please save the date for our special day. Formal invitation to follow.

We can't wait to celebrate with you!

With love,
${user.partner1_name} & ${user.partner2_name}`
    },
    {
      name: 'RSVP Reminder',
      subject: 'RSVP Reminder - Our Wedding',
      body: `Dear [Guest Name],

We hope you received our wedding invitation! We'd love to know if you'll be able to join us on our special day.

Please RSVP at your earliest convenience so we can finalize our arrangements.

Looking forward to celebrating with you!

Warm regards,
${user.partner1_name} & ${user.partner2_name}`
    },
    {
      name: 'Thank You',
      subject: 'Thank You for Celebrating With Us',
      body: `Dear [Guest Name],

Thank you so much for being part of our special day! Your presence meant the world to us.

We are so grateful to have you in our lives and to have shared this moment with you.

With love and gratitude,
${user.partner1_name} & ${user.partner2_name}`
    }
  ];

  const applyTemplate = (template) => {
    setEmailSubject(template.subject);
    setEmailBody(template.body);
  };

  // Group guests by RSVP status
  const confirmedGuests = guests.filter(g => g.rsvp_status === 'confirmed');
  const pendingGuests = guests.filter(g => g.rsvp_status === 'pending');
  const declinedGuests = guests.filter(g => g.rsvp_status === 'declined');
  const guestsWithEmail = guests.filter(g => g.email).length;

  // Collapsible guest group component
  const GuestGroup = ({ title, guestList, icon: Icon, iconColor, sectionKey }) => {
    const isExpanded = expandedSections[sectionKey];
    const guestIds = guestList.map(g => g.id);
    const selectedInGroup = guestIds.filter(id => selectedGuests.includes(id)).length;
    
    if (guestList.length === 0) return null;
    
    return (
      <div className="border border-stone-200 rounded-xl overflow-hidden mb-3">
        {/* Header - always visible */}
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown size={20} className="text-stone-400" />
            ) : (
              <ChevronRight size={20} className="text-stone-400" />
            )}
            <Icon size={20} className={iconColor} />
            <span className="font-medium text-[#1C1917]">{title}</span>
            <span className="text-sm text-[#57534E] bg-white px-2 py-0.5 rounded-full">
              {guestList.length}
            </span>
          </div>
          {selectedInGroup > 0 && (
            <span className="text-xs text-[#881337] font-medium">
              {selectedInGroup} selected
            </span>
          )}
        </button>
        
        {/* Expandable content */}
        {isExpanded && (
          <div className="border-t border-stone-200">
            {/* Select all in group */}
            <div className="px-4 py-2 bg-white border-b border-stone-100">
              <button
                onClick={() => handleSelectGroup(guestIds)}
                className="text-xs text-[#881337] hover:underline"
              >
                {guestIds.every(id => selectedGuests.includes(id)) ? 'Deselect group' : 'Select all in group'}
              </button>
            </div>
            
            {/* Guest list */}
            <div className="max-h-48 overflow-y-auto">
              {guestList.map(guest => (
                <label
                  key={guest.id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 cursor-pointer transition-colors border-b border-stone-50 last:border-0"
                >
                  <input
                    type="checkbox"
                    checked={selectedGuests.includes(guest.id)}
                    onChange={() => handleSelectGuest(guest.id)}
                    className="w-4 h-4 rounded border-stone-300 text-[#881337] focus:ring-[#881337]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#1C1917] truncate">{guest.name}</div>
                    {guest.email && (
                      <div className="text-xs text-[#57534E] truncate">{guest.email}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Preview of personalized email
  const getPreviewText = () => {
    if (!emailBody) return '';
    const firstSelectedGuest = guests.find(g => selectedGuests.includes(g.id));
    if (!firstSelectedGuest) return emailBody;
    return emailBody.replace(/\[Guest Name\]/g, firstSelectedGuest.name);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
      <div className="bg-white/70 backdrop-blur-xl border-b border-stone-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Button
            data-testid="back-to-dashboard-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#57534E] hover:text-[#881337]"
          >
            <ArrowLeft className="mr-2" size={18} />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-serif font-light tracking-tight text-[#1C1917] mb-2">
            Guest Relationship Manager
          </h1>
          <p className="text-[#57534E]">Send personalized updates and communications to your guests</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-5 border border-white/50">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-[#881337]" size={22} />
              <span className="text-2xl font-serif text-[#1C1917]">{guests.length}</span>
            </div>
            <p className="text-sm text-[#57534E]">Total Guests</p>
          </div>
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-5 border border-white/50">
            <div className="flex items-center justify-between mb-2">
              <Mail className="text-[#881337]" size={22} />
              <span className="text-2xl font-serif text-[#1C1917]">{guestsWithEmail}</span>
            </div>
            <p className="text-sm text-[#57534E]">With Email</p>
          </div>
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-5 border border-white/50">
            <div className="flex items-center justify-between mb-2">
              <UserCheck className="text-[#84A98C]" size={22} />
              <span className="text-2xl font-serif text-[#1C1917]">{confirmedGuests.length}</span>
            </div>
            <p className="text-sm text-[#57534E]">Confirmed</p>
          </div>
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-5 border border-white/50">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="text-amber-500" size={22} />
              <span className="text-2xl font-serif text-[#1C1917]">{pendingGuests.length}</span>
            </div>
            <p className="text-sm text-[#57534E]">Pending</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Guest Selection - Collapsible */}
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif text-[#1C1917]">Select Recipients</h2>
              {guests.length > 0 && (
                <Button
                  onClick={handleSelectAll}
                  variant="outline"
                  size="sm"
                  className="rounded-full text-xs"
                >
                  {selectedGuests.length === guests.length ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </div>

            {/* Collapsible Guest Groups */}
            {guests.length > 0 ? (
              <div className="space-y-1">
                <GuestGroup
                  title="Confirmed"
                  guestList={confirmedGuests}
                  icon={UserCheck}
                  iconColor="text-[#84A98C]"
                  sectionKey="confirmed"
                />
                <GuestGroup
                  title="Pending RSVP"
                  guestList={pendingGuests}
                  icon={CheckCircle}
                  iconColor="text-amber-500"
                  sectionKey="pending"
                />
                <GuestGroup
                  title="Declined"
                  guestList={declinedGuests}
                  icon={UserX}
                  iconColor="text-stone-400"
                  sectionKey="declined"
                />
                
                {/* Show all guests if no status grouping */}
                {confirmedGuests.length === 0 && pendingGuests.length === 0 && declinedGuests.length === 0 && (
                  <GuestGroup
                    title="All Guests"
                    guestList={guests}
                    icon={Users}
                    iconColor="text-[#881337]"
                    sectionKey="all"
                  />
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="mx-auto text-stone-300 mb-4" size={48} />
                <p className="text-[#57534E] mb-4">No guests added yet</p>
                <Button
                  onClick={() => navigate('/guests')}
                  className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
                >
                  Add Guests
                </Button>
              </div>
            )}

            {selectedGuests.length > 0 && (
              <div className="mt-4 pt-4 border-t border-stone-200">
                <p className="text-sm font-medium text-[#881337]">
                  {selectedGuests.length} guest{selectedGuests.length !== 1 ? 's' : ''} selected
                </p>
              </div>
            )}
          </div>

          {/* Email Composer */}
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/50">
            <h2 className="text-2xl font-serif text-[#1C1917] mb-6">Compose Message</h2>

            {/* Email Templates */}
            <div className="mb-6">
              <p className="text-sm text-[#57534E] mb-3">Quick Templates:</p>
              <div className="flex flex-wrap gap-2">
                {emailTemplates.map(template => (
                  <Button
                    key={template.name}
                    onClick={() => applyTemplate(template)}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs"
                  >
                    {template.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Personalization hint */}
            <div className="mb-4 p-3 bg-rose-50 rounded-xl">
              <p className="text-xs text-[#881337]">
                💡 Use <code className="bg-white px-1 rounded">[Guest Name]</code> in your message - it will be automatically replaced with each guest's actual name.
              </p>
            </div>

            {/* Subject */}
            <div className="mb-4">
              <label htmlFor="subject" className="text-sm font-medium text-[#1C1917] mb-2 block">Subject</label>
              <Input
                id="subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject"
                className="border border-stone-300 rounded-xl px-4"
              />
            </div>

            {/* Body */}
            <div className="mb-4">
              <label htmlFor="body" className="text-sm font-medium text-[#1C1917] mb-2 block">Message</label>
              <Textarea
                id="body"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Your message to guests... Use [Guest Name] for personalization"
                className="min-h-40 border border-stone-300 rounded-xl p-4"
              />
            </div>

            {/* Preview */}
            {emailBody && selectedGuests.length > 0 && (
              <div className="mb-4 p-4 bg-stone-50 rounded-xl">
                <p className="text-xs font-medium text-[#57534E] mb-2">Preview (first recipient):</p>
                <p className="text-sm text-[#1C1917] whitespace-pre-wrap">{getPreviewText()}</p>
              </div>
            )}

            {/* Send Button */}
            <Button
              onClick={handleSendEmails}
              disabled={sending || selectedGuests.length === 0 || !emailSubject || !emailBody}
              className="w-full rounded-full py-6 bg-[#881337] hover:bg-[#9F1239] text-white disabled:opacity-50"
            >
              {sending ? (
                'Sending...'
              ) : (
                <>
                  <Send className="mr-2" size={18} />
                  Send to {selectedGuests.length || 0} Guest{selectedGuests.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestRelationshipManager;
